<template>
  <div>
    <digital-event-preloader type="container" :active="roomLoading" />
    <div v-if="!roomLoading" class="room-container">
      <section class="ev-digital-event-room-row section-panel">
        <div class="video-room-chat">
          <div class="left-panel">
            <digital-event-room-presentation
              :pinnedOptions="{ height: 'auto', width: '59vw', top: '0', left: '0', bottom: '0' }"
            />
          </div>
          <div
            v-if="userTable"
            id="people-chat-container"
            class="right-panel"
            :class="'table-' + userTable.TABLE_TYPE.toLowerCase()"
          >
            <ev-tabs :options="{ tabClass: 'tab-header-links' }">
              <ev-tab name="Chat" id="tab-chat" :counter="unreadMessagesCounter.TABLE + unreadMessagesCounter.PRIVATE">
                <digital-event-room-chat></digital-event-room-chat>
              </ev-tab>
              <ev-tab name="People" id="tab-people">
                <digital-event-room-people></digital-event-room-people>
              </ev-tab>
              <ev-tab
                name="Schedule"
                id="tab-schedule"
                v-show="room && room.ADD_SCHEDULE"
                :isVisible="room.ADD_SCHEDULE"
                class="right schedule"
              >
                <digital-event-room-schedule></digital-event-room-schedule>
              </ev-tab>
              <ev-tab name="Me" class="right me">
                <digital-event-user-profile></digital-event-user-profile>
              </ev-tab>
            </ev-tabs>
          </div>
        </div>
      </section>

      <div class="order-container">
        <digital-event-room-photo-wall v-if="photoWallEnabled"></digital-event-room-photo-wall>

        <!-- SPONSOR PREMIUM SLIDER -->
        <section
          v-if="room.USE_PREMIUM_SLIDER && room.children.Slider && room.children.Slider.length"
          class="row ev-digital-event-room-row sponsors-premium"
          :style="{ order: premiumSliderOrder }"
        >
          <div v-for="slider in room.children.Slider" :key="'slider-' + slider.uuid">
            <digital-event-room-sponsor-slider
              isPremium
              :slider="slider"
              :items="slider.children.Slideritem"
              :order-by="slider.SORT_TYPE"
              :options="{ type: 'slider' }"
              children="SponsorPublicProfile"
            ></digital-event-room-sponsor-slider>
          </div>
        </section>

        <!-- SPONSOR SLIDER -->
        <section
          v-if="room.USE_SPONSOR_SIDER && room.children.SponsorProfileList && room.children.SponsorProfileList.length"
          class="row ev-digital-event-room-row sponsors-standard"
          :style="{ order: standartSliderOrder }"
        >
          <div v-for="slider in room.children.SponsorProfileList" :key="'slider-' + slider.uuid">
            <digital-event-room-sponsor-slider
              :slider="slider"
              :items="slider.children.SponsorPublicProfile"
              :order-by="slider.SORT_TYPE"
              :options="{ type: 'spl' }"
              children="SponsorPublicProfile"
            ></digital-event-room-sponsor-slider>
          </div>
        </section>

        <!-- SPONSOR TABLES -->
        <section v-if="sponsorTables.length" class="row ev-digital-event-room-row tables sponsor">
          <digital-event-room-table-list
            tableGroupName="Sponsors"
            :searchable="true"
            :paginated="true"
            :options="{ group_title: 'highlight', table_class: 'premium', group_prefix: 'S' }"
            :tables="sponsorTables"
          ></digital-event-room-table-list>
        </section>
      </div>

      <!-- VIP TABLES -->
      <section v-if="vipTables.length" class="row ev-digital-event-room-row tables vip">
        <digital-event-room-table-list
          tableGroupName="VIPs"
          :searchable="true"
          :paginated="true"
          :options="{ table_class: 'premium', group_prefix: 'V' }"
          :tables="vipTables"
        ></digital-event-room-table-list>
      </section>

      <!-- In Person Guests TABLES -->
      <section v-if="inPersonGuests.length" class="row ev-digital-event-room-row tables guest">
        <digital-event-room-table-list
          tableGroupName="In Person Guests"
          :searchable="true"
          :paginated="true"
          :options="{ table_class: 'regular', group_prefix: 'G' }"
          :tables="inPersonGuests"
        ></digital-event-room-table-list>
      </section>

      <!-- GUEST TABLES -->
      <section v-if="guestTables.length" class="row ev-digital-event-room-row tables guest">
        <digital-event-room-table-list
          tableGroupName="Virtual Guests"
          :searchable="true"
          :paginated="true"
          :options="{ table_class: 'regular', group_prefix: 'G' }"
          :tables="guestTables"
        ></digital-event-room-table-list>
      </section>

      <!-- PUBLIC GALLERY -->
      <section v-if="publicGalleryIsAvailable()" class="row ev-digital-event-room-row public-gallery">
        <digital-event-room-public-gallery></digital-event-room-public-gallery>
      </section>
    </div>
  </div>
</template>

<script>
import RoomMixin from "./mixins/RoomMixin";
import PublicGallery from "./Tables/PublicGallery";
import Schedule from "./Schedule";
import TableList from "./Tables/TableList";
import Presentation from "@/components/DigitalEvent/RoomList/Room/Presentation";
import SponsorSlider from "@/components/DigitalEvent/RoomList/Room/SponsorSliderSlick";
import Chat from "@/components/DigitalEvent/RoomList/Room/Chat";
import People from "./People";
import Profile from "@/components/DigitalEvent/Auth/Profile";
import Tabs from "@/components/DigitalEvent/common/Tabs";
import Tab from "@/components/DigitalEvent/common/Tab";
import PhotoWall from "@/components/DigitalEvent/RoomList/Room/PhotoWall/PhotoWall";
import { scrollToTheBottom } from "@/helpers/DOMHelper";
import { mapGetters, mapState } from "vuex";
import { ROUTES, SPONSOR_SLIDER_POSITION } from "@/helpers/constants";
import { shadeColor, getThemeColor } from "@/helpers/helpers";

export default {
  name: "Room",
  mixins: [RoomMixin],
  components: {
    "digital-event-room-public-gallery": PublicGallery,
    "digital-event-room-schedule": Schedule,
    "digital-event-user-profile": Profile,
    "digital-event-room-sponsor-slider": SponsorSlider,
    "digital-event-room-table-list": TableList,
    "digital-event-room-chat": Chat,
    "digital-event-room-people": People,
    "digital-event-room-presentation": Presentation,
    "digital-event-room-photo-wall": PhotoWall,
    "ev-tabs": Tabs,
    "ev-tab": Tab
  },
  data() {
    return {
      roomLoading: false
    };
  },
  computed: {
    ...mapState({
      event: state => state.event.context
    }),
    ...mapGetters({
      userTable: "tables/userTable",
      sponsorTables: "tables/sponsorTables",
      vipTables: "tables/vipTables",
      guestTables: "tables/guestTables",
      publicTable: "tables/publicTable",
      inPersonGuests: "tables/inPersonGuests"
    }),
    roomState() {
      return this.$store.getters["room/roomState"];
    },
    mainSponsors() {
      return this.$store.getters["room/mainSponsors"];
    },
    premiumSponsors() {
      return this.$store.getters["room/premiumSponsors"];
    },
    unreadMessagesCounter() {
      return this.$store.state.chat.unreadMessagesCounter;
    },
    photoWallEnabled() {
      return this.room.PHOTO_WALL_ENABLED;
    },
    standartSliderOrder() {
      return SPONSOR_SLIDER_POSITION[this.room.POSITION_STANDARD_SLIDER];
    },
    premiumSliderOrder() {
      return SPONSOR_SLIDER_POSITION[this.room.POSITION_PREMIUM_SLIDER];
    }
  },
  methods: {
    /**
     * Check that public gallery is available
     * @return {*|number}
     */
    publicGalleryIsAvailable() {
      return this.publicTable && this.$store.getters["tables/tableUsers"](this.publicTable.uuid).length;
    },
    setThemeColors() {
      let root = document.documentElement;
      if (this.room.ROOM_COLOUR_TYPE === "custom" && this.room.ROOM_COLOUR) {
        root.style.setProperty("--room-color", `${this.room.ROOM_COLOUR}`);
        root.style.setProperty("--room-color-lighten", `${shadeColor(this.room.ROOM_COLOUR, 30)}`);
        root.style.setProperty("--room-color-darken", `${shadeColor(this.room.ROOM_COLOUR, -30)}`);
      } else {
        root.style.setProperty("--room-color", `var(--color-primary)`);
        root.style.removeProperty("--room-color-lighten");
        root.style.removeProperty("--room-color-darken");
      }
      this.$store.commit("setThemeStyles", {
        primaryColor: getThemeColor("--room-color")
      });
    }
  },
  watch: {
    receivedInvites(newValue) {
      if (newValue) {
        this.showReceivedInvites(newValue);
      }
    },
    photoWallEnabled(newVal) {
      if (!newVal) {
        document.body.style.overflow = "auto";
      }
    },
    "room.ROOM_COLOUR"(newValue) {
      if (newValue) {
        this.setThemeColors();
      }
    },
    "room.SLUG"(newValue) {
      if (newValue) {
        this.$router.push({ name: ROUTES.DIGITAL_EVENT_ROOM, params: { roomSlug: newValue } });
      }
    }
  },
  async created() {
    this.roomLoading = true;
    await this.$store.dispatch("tables/getOrCreateUserTable");
    await this.$store.dispatch("invites/getInvites");
    await this.$store.dispatch("socket/startStatePulling");
    this.setThemeColors();
    this.roomLoading = false;
  },
  async mounted() {
    scrollToTheBottom(`group-${this.room.uuid}-messages`);

    this.showReceivedInvites(this.receivedInvites);
  }
};
</script>
